import { useActionState, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { TriangleAlert } from 'lucide-react'
import { useGroup } from '../hooks/useGroups'
import { usePageTitle } from '../hooks/usePageTitle'
import { LEGACY_PRODUCT_NAME } from '../lib/product'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../lib/api'
import type { CreateSessionBody, GroupDetail, SessionCreatedResponse } from '../lib/types'
import { saveSessionSnapshot } from '../lib/sessionSnapshot'
import {
  SessionBuilderProvider,
  useSessionBuilder,
} from '../contexts/SessionBuilderContext'
import { RosterStep } from '../components/wizard/RosterStep'
import { VibeStep } from '../components/wizard/VibeStep'
import { Card, Spinner, Stepper } from '../components/ui'

const WIZARD_STEPS = [
  { id: 'roster', label: 'Roster' },
  { id: 'vibe', label: 'Vibe' },
  { id: 'results', label: 'Results' },
] as const

type WizardStep = (typeof WIZARD_STEPS)[number]['id']

function buildVibeText(chips: string[], text: string): string | undefined {
  const parts = [text.trim(), chips.join(', ').trim()].filter(Boolean)
  const joined = parts.join(' — ')
  return joined || undefined
}

function Wizard({ group, groupId }: { group: GroupDetail; groupId: string }) {
  const { state } = useSessionBuilder()
  const navigate = useNavigate()
  const [step, setStep] = useState<WizardStep>('roster')

  // The recommend submit: pending + result + error in one hook (design-system §6.0).
  const [actionError, submitAction, pending] = useActionState<string | null, void>(async () => {
    try {
      const body: CreateSessionBody = {
        group_id: groupId,
        roster_user_ids: state.rosterUserIds,
        // Everyone semantics: the roster IS exactly who's playing tonight, so
        // coverage is always "every selected player" (backend clamps safely).
        coverage_k: state.rosterUserIds.length,
        togetherness_mode: state.togethernessMode,
        togetherness_required: state.togethernessRequired,
        vibe_text: buildVibeText(state.vibeChips, state.vibeText),
        wildcard_opt_in: state.wildcardOptIn,
        client: 'web',
      }
      const resp = await api.post<SessionCreatedResponse>('/sessions', body)
      saveSessionSnapshot(resp.session.id, body)
      navigate({ to: '/steam/sessions/$sessionId', params: { sessionId: resp.session.id } })
      return null
    } catch {
      return "We couldn't start that session. Try rolling again."
    }
  }, null)

  return (
    <>
      {/* The wizard's page-level heading (WCAG 1.3.1: no route starts at h2) —
          sr-only so the visual hierarchy stays the step titles', and the
          route-change focus handoff has a proper target. */}
      <h1 className="sr-only">Find tonight&apos;s game</h1>
      <div className="mx-auto max-w-3xl space-y-6">
        <Stepper
          steps={WIZARD_STEPS}
          activeIndex={WIZARD_STEPS.findIndex((s) => s.id === step)}
          label="Session steps"
        />

        <Card elevation="raised" className="p-6 sm:p-8" glow={false}>
          {step === 'roster' && <RosterStep members={group.members} onNext={() => setStep('vibe')} />}
          {step === 'vibe' && (
            <>
              <VibeStep pending={pending} onBack={() => setStep('roster')} onRoll={() => submitAction()} />
              {/* Danger pairing (§2.5): icon + danger/12 tint, announced — never
                  a bare red line floating under the footer. */}
              {actionError && (
                <div
                  role="alert"
                  className="mt-6 flex items-start gap-2 rounded-control bg-danger/12 p-3 ring-1 ring-danger/40"
                >
                  <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-danger-fg" strokeWidth={1.75} aria-hidden="true" />
                  <p className="text-sm text-danger-fg">{actionError}</p>
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </>
  )
}

export function PlayScreen({ groupId }: { groupId: string }) {
  usePageTitle("Find tonight's game", LEGACY_PRODUCT_NAME)
  const { group, isLoading, error } = useGroup(groupId)
  const { user } = useAuth()

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    )
  }
  if (error || !group) {
    return <p className="text-sm text-danger-fg">{error ?? 'Group not found.'}</p>
  }

  // Auto-select self: the signed-in user is always a member of the group, so
  // pre-seed the roster with them (guarded to ids that exist in group.members).
  const selfId = group.members.find((m) => m.user_id === user?.id)?.user_id
  const initial = selfId ? { groupId, rosterUserIds: [selfId] } : { groupId }

  return (
    <SessionBuilderProvider initial={initial}>
      <Wizard group={group} groupId={groupId} />
    </SessionBuilderProvider>
  )
}
